import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import CollectibleForm from "@/components/CollectibleForm";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ImageOff, Lock, Tag, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function CreatorStudio() {
  const { user } = useAuth();
  const isCreator = user?.role === "creator";
  const [collectibles, setCollectibles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [cRes, catRes] = await Promise.all([
        api.get(`/collectibles?owner_id=${user.user_id}`),
        api.get("/categories"),
      ]);
      setCollectibles(cRes.data);
      setCategories(catRes.data);
    } catch {
      setCollectibles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryName = (id) => categories.find((c) => c.id === id)?.name;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/collectibles/${deleteTarget.id}`);
      toast.success("Collectible deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!isCreator) {
    return (
      <div className="relative min-h-screen">
        <div className="glow-bg" />
        <Header />
        <div className="relative z-10 pt-40 flex items-center justify-center px-6" data-testid="studio-access-denied">
          <div className="border border-white/10 bg-black/60 backdrop-blur-xl p-12 max-w-md text-center">
            <Lock className="w-10 h-10 text-glow-secondary mx-auto mb-6" />
            <h1 className="font-heading font-700 text-2xl uppercase tracking-tight text-white">No Gallery Access</h1>
            <p className="font-mono text-sm text-white/50 mt-4 leading-relaxed">
              Your account ({user?.email}) hasn't been granted a Creator gallery. Ask the Super Admin for access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="glow-bg" />
      <Header />
      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 border-b border-white/10 pb-6">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-accent">
              Creator Studio — {user?.name}
            </span>
            <h1 className="font-heading font-900 text-4xl md:text-5xl tracking-tighter text-white mt-3">My Gallery</h1>
          </div>
          <div className="flex gap-3">
            <Link to={`/profile/${user.user_id}`}>
              <Button
                variant="outline"
                className="border-glow-primary/50 text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono uppercase tracking-wider h-11"
                data-testid="view-public-profile-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> View Profile
              </Button>
            </Link>
            <Button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono uppercase tracking-wider h-11 shadow-[0_0_20px_rgba(0,240,255,0.3)]"
              data-testid="studio-new-collectible-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Collectible
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="h-64 bg-surface border border-white/10 animate-pulse" />)}
          </div>
        ) : collectibles.length === 0 ? (
          <div className="border border-dashed border-white/15 py-32 text-center" data-testid="studio-empty">
            <p className="font-heading font-500 text-xl text-white/70 uppercase tracking-widest">Empty Gallery</p>
            <p className="font-mono text-sm text-white/40 mt-3">Add your first collectible to build your gallery.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="studio-grid">
            {collectibles.map((c, i) => {
              const cover = c.photos?.[0];
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                  className="bg-surface border border-white/10 overflow-hidden group hover:border-glow-primary/40 transition-colors"
                  data-testid={`studio-card-${c.id}`}
                >
                  <div className="aspect-video bg-black overflow-hidden relative">
                    {cover ? (
                      <img src={fileUrl(cover.storage_path)} alt={c.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20"><ImageOff className="w-8 h-8" /></div>
                    )}
                    <span className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-1 font-mono text-[10px] text-glow-primary uppercase tracking-wider">
                      {c.photos?.length || 0}/6 Photos
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {c.category_id && categoryName(c.category_id) ? (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-glow-accent border border-glow-accent/40 px-2 py-0.5 flex items-center gap-1">
                          <Tag className="w-2.5 h-2.5" /> {categoryName(c.category_id)}
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-white/30 border border-white/10 px-2 py-0.5">Uncategorized</span>
                      )}
                    </div>
                    <h3 className="font-heading font-700 text-lg text-white tracking-tight truncate">{c.title}</h3>
                    <p className="font-mono text-xs text-white/50 mt-2 line-clamp-2">{c.description}</p>
                    <div className="flex gap-2 mt-5">
                      <Button
                        onClick={() => { setEditing(c); setFormOpen(true); }}
                        variant="outline"
                        className="flex-1 border-glow-primary/50 text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono text-xs uppercase tracking-wider"
                        data-testid={`studio-edit-btn-${c.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                      </Button>
                      <Button
                        onClick={() => setDeleteTarget(c)}
                        variant="outline"
                        className="border-glow-secondary/50 text-glow-secondary hover:bg-glow-secondary/10 rounded-none font-mono text-xs uppercase tracking-wider"
                        aria-label="Delete collectible"
                        data-testid={`studio-delete-btn-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <CollectibleForm
        open={formOpen}
        editing={editing}
        categories={categories}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={load}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-surface border border-white/15 rounded-none" data-testid="studio-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-tight text-white">Delete Collectible?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-sm text-white/50">
              This permanently removes "{deleteTarget?.title}" from your gallery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 text-white/70 hover:bg-white/5 rounded-none font-mono uppercase tracking-wider">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-glow-secondary text-white hover:bg-glow-secondary/80 rounded-none font-mono uppercase tracking-wider"
              data-testid="studio-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
